// English funnel copy. `el.ts` is typed against this object, so a missing or
// misspelled Greek key is a compile error rather than a blank screen.
//
// Copy guardrails (non-negotiable — see docs/web-to-app-funnel-v2-plan.md):
//   · No personality types, compatibility scores, therapy or psychological claims.
//   · No fabricated social proof. Pre-launch means there is no honest user count,
//     so we do not show one. The waitlist position is a real number.
//   · Observational framing only: reflect back what they told us, never diagnose.

export const en = {
  brand: 'Bonded',
  language: 'ΕΛ',
  back: 'Back',
  continue: 'Continue',
  skip: 'Skip',
  loading: 'Making space for a better moment…',
  retry: 'Try again',
  stepOf: 'Question {{n}} of {{total}}',

  consent: {
    title: 'A small choice before we begin',
    body: 'We only store what this page needs to work. With your permission we would also measure how the flow is used, so we can improve it. Nothing optional loads unless you choose it.',
    accept: 'Accept analytics',
    reject: 'Only necessary',
    settings: 'Cookie settings',
    cookiePolicy: 'Cookie Policy',
    privacyPolicy: 'Privacy Policy',
  },

  footer: {
    copyright: '© {{year}} Bonded Studio. Powered by Sapphire Labs.',
  },

  landing: {
    cta: 'Build our first ritual',
    ctaWaitlist: 'Join the First Circle',
    note: '90 seconds · no payment',
    agreementPrefix: 'By continuing, you agree to our',
    terms: 'Terms',
    and: 'and',
    privacy: 'Privacy Policy',
    agreementError: 'We couldn’t record your agreement. Please try again.',
    researchOptIn:
      'Optional: let my answers help Bonded’s research on connection. Anonymous, never sold, and you can withdraw them any time.',
  },

  // Hero variants, keyed by ?app= — mirrors the SEO landing pages.
  audiences: {
    'for-anyone': {
      pill: 'BUILT FOR TWO · EARLY ACCESS FIRST',
      title: 'An app that starts the conversation.',
      sub: 'Same question, two phones. Pick a deck, set the depth, and let the app step back once you’re talking.',
    },
    'for-couples': {
      pill: 'FOR COUPLES · EARLY ACCESS FIRST',
      title: 'An app that starts the conversation.',
      sub: 'For the nights that turn into logistics. One question you both answer at once — and no one has to be the one who gets deep.',
    },
    'for-first-dates': {
      pill: 'FOR FIRST DATES · EARLY ACCESS FIRST',
      title: 'Never run out of things to say.',
      sub: 'Same question, two phones. No interview, no scripts — and either of you can pass anything.',
    },
    'for-friends': {
      pill: 'FOR FRIENDS · EARLY ACCESS FIRST',
      title: 'Better than the usual catch-up.',
      sub: 'Same question, two phones. Playful before personal, and you both set how far it goes.',
    },
  },

  // Composed visuals on the value beats.
  visual: {
    answer: 'Answer',
    pass: 'Pass',
    compare: {
      beforeTag: 'Most nights',
      beforeQ: '“How was your day?”',
      beforeA: '“Fine. You?”',
      afterTag: 'With Bonded',
      afterQ: '“What’s something you changed your mind about this year?”',
    },
    prompt: {
      tag: 'Both of you see this',
      example: '“What’s a small thing I do that you’ve never mentioned?”',
      caption: 'Pass is always there. No one has to say why.',
    },
    pace: { label: 'Your depth', offTable: 'Off the table' },
  },

  exit: {
    title: 'Thanks for being honest.',
    body: 'Bonded is made for adults. We’ll be here when you’re 18.',
  },

  questions: {
    connection_context: {
      title: 'Who are you here with?',
      options: {
        partner: 'My partner',
        date: 'A date',
        friend: 'A friend',
        new_person: 'Someone new',
        unsure: 'Not sure yet',
      },
    },
    age_band: {
      title: 'And how old are you?',
      body: 'Bonded is made for adults.',
      options: {
        '18_24': '18–24',
        '25_34': '25–34',
        '35_44': '35–44',
        '45_54': '45–54',
        '55_64': '55–64',
        '65_plus': '65+',
        under_18: 'I’m under 18',
      },
    },
    together_length: {
      title: 'How long have you been together?',
      options: {
        under_1y: 'Less than a year',
        '1_3y': '1–3 years',
        '3_7y': '3–7 years',
        over_7y: 'More than 7 years',
      },
    },
    dating_stage: {
      title: 'Where are you two at?',
      options: {
        not_met_yet: 'We haven’t met yet',
        first_date_soon: 'First date coming up',
        few_dates_in: 'A few dates in',
        just_started_seeing: 'Just started seeing each other',
      },
    },
    friend_length: {
      title: 'How long have you known them?',
      options: {
        new_friend: 'We’re new friends',
        few_years: 'A few years',
        since_forever: 'Since forever',
      },
    },
    connection_goal: {
      title: 'What would make this feel worth it?',
      options: {
        fun: 'Laugh together',
        closeness: 'Feel closer',
        understanding: 'Understand each other better',
        growth: 'Go somewhere deeper',
      },
    },
    main_barrier: {
      title: 'What usually gets in the way?',
      options: {
        questions: 'Not knowing what to ask',
        distraction: 'Phones and distractions',
        time_energy: 'Low time or energy',
        repetition: 'The same conversations',
        awkward_vulnerability: 'Vulnerability feels awkward',
        none: 'Nothing in particular',
      },
    },
    last_real_conversation: {
      title: 'When did you last talk about something that wasn’t logistics?',
      options: {
        this_week: 'This week',
        this_month: 'This month',
        cant_remember: 'Honestly, can’t remember',
        we_talk_like_this_often: 'We talk like this often',
      },
    },
    first_date_worry: {
      title: 'What’s the part you’d rather avoid?',
      options: {
        running_out_of_things: 'Running out of things to say',
        feels_like_interview: 'It feeling like an interview',
        being_too_much: 'Being “too much”',
        nothing_really: 'Nothing really',
      },
    },
    catchup_pattern: {
      title: 'What do your catch-ups usually look like?',
      options: {
        same_topics: 'The same topics every time',
        we_go_deep: 'We go pretty deep',
        depends: 'Depends on the day',
      },
    },
    intentional_time_frequency: {
      title: 'How often do you make time like this?',
      options: {
        daily: 'Most days',
        weekly: 'Weekly',
        monthly: 'Monthly',
        rarely: 'Rarely',
        first_time: 'This is new for us',
      },
    },
    social_energy: {
      title: 'What’s the energy right now?',
      options: { soft: 'Soft', curious: 'Curious', playful: 'Playful', bold: 'Bold' },
    },
    intended_setting: {
      title: 'Where will you use this?',
      options: {
        home: 'At home',
        date: 'On a date',
        travelling_walking: 'Walking or travelling',
        friends: 'With friends',
        later: 'Saving it for later',
      },
    },
    available_time: {
      title: 'How much time feels right?',
      options: {
        '5_10': '5–10 minutes',
        '15_20': '15–20 minutes',
        '30_45': '30–45 minutes',
        flexible: 'We’re flexible',
      },
    },
    preferred_depth: {
      title: 'How deep should it go?',
      options: { '1': 'Light', '2': 'Playful', '3': 'Deep', '4': 'Bold' },
    },
    topic_boundaries: {
      title: 'Anything you’d rather leave out?',
      body: 'Optional. Choose as many as you need.',
      done: 'Nothing to leave out',
      options: {
        exes: 'Exes',
        money: 'Money',
        family: 'Family',
        future: 'The future',
        sex_intimacy: 'Sex & intimacy',
        religion: 'Religion',
        politics: 'Politics',
        prefer_not_to_say: 'Prefer not to say',
      },
    },
    scan_trigger: {
      title: 'What made you stop and try this?',
      options: {
        social_video: 'Saw it in a video',
        friend_told_me: 'A friend told me',
        card: 'A card I was given',
        time_together: 'We have time together',
        reconnecting: 'We want to reconnect',
        repeated_conversation: 'Conversation feels repetitive',
        first_meeting: 'It’s a first meeting or date',
        curiosity: 'Just curious',
      },
    },
    return_value: {
      title: 'What would make Bonded worth keeping?',
      options: {
        experiences: 'New Experiences',
        guided_rituals: 'Guided rituals',
        memory_capsules: 'Memory Capsules',
        insights: 'Insights about our time together',
        unlimited_hosting: 'Unlimited hosting',
      },
    },
  },

  value: {
    right_question: {
      eyebrow: 'Why this works',
      title: 'The right question does the work.',
      body: 'It’s not a closeness problem. It’s a starting problem.',
      cta: 'Keep going',
    },
    your_pace: {
      eyebrow: 'Before we build it',
      title: 'Your limits are part of the design.',
      body: 'What you flagged stays off the table.',
      cta: 'Build my ritual',
    },
    reassurance: {
      cta: 'Makes sense',
      variants: {
        comfort: {
          eyebrow: 'About that',
          title: 'You decide how far it goes.',
          body: 'Every question has a pass. Nobody explains why.',
        },
        we_bring_questions: {
          eyebrow: 'About that',
          title: 'You’ll never have to think one up.',
          body: 'Bonded brings the question. You both just answer it.',
        },
        ten_minutes: {
          eyebrow: 'About that',
          title: 'Ten minutes is enough.',
          body: 'One question, both of you. That’s the whole thing.',
        },
        never_the_same: {
          eyebrow: 'About that',
          title: 'You won’t get the same night twice.',
          body: 'Built from your mood, time and limits — never the same twice.',
        },
        not_an_interview: {
          eyebrow: 'About that',
          title: 'Nobody’s being interviewed.',
          body: 'Neither of you is the one asking.',
        },
        general: {
          eyebrow: 'Why this works',
          title: 'Neither of you has to lead.',
          body: 'The question carries the moment, not one of you.',
        },
      },
    },
  },

  howItWorks: {
    howitworks_together: {
      eyebrow: 'How it works · 1',
      title: 'Pick the mood. Send them a link.',
      body: 'They join from their phone. No account, nothing to install.',
      cta: 'Then what?',
    },
    howitworks_moment: {
      eyebrow: 'How it works · 2',
      title: 'A different world for every kind of closeness.',
      body: 'Answer, or pass. Flag what lands.',
      cta: 'Got it',
    },
  },

  insight: {
    eyebrow: 'Your ritual is taking shape',
    chartLabel: 'Recommended question balance',
    note: 'A recommendation from your choices — not a score.',
    cta: 'Shape the rest',
    labels: { playful: 'Playful', warm: 'Warm', deep: 'Deep', bold: 'Bold' },
    goals: {
      fun: {
        title: 'Play opens the door.',
        body: 'Starting light, then one warmer moment.',
      },
      closeness: {
        title: 'Closeness starts softly.',
        body: 'Warm first, then the things worth saying.',
      },
      understanding: {
        title: 'Comfort comes before chemistry.',
        body: 'Warm and playful, with no pressure to perform.',
      },
      growth: {
        title: 'Depth works best when it feels earned.',
        body: 'Building gradually, so depth feels earned.',
      },
    },
  },

  crafting: {
    eyebrow: 'One moment',
    title: 'Crafting your ritual…',
    body: 'Shaping every question around what you just told us.',
    goal: 'Reading your goal — {{value}}',
    context: 'Tuning it for {{value}}',
    energy: 'Matching your energy — {{value}}',
    boundaries_one: 'Respecting your boundary — {{count}} set',
    boundaries_other: 'Respecting your boundaries — {{count}} set',
    depth: 'Setting your pace — {{value}}',
  },

  gate: {
    eyebrow: 'Ready',
    title: 'Your ritual is ready.',
    body: 'Where should we send it?',
    google: 'Continue with Google',
    or: 'or use your email',
    email: 'Email',
    emailPlaceholder: 'you@email.com',
    name: 'What should we call you?',
    cta: 'Show me my ritual',
    working: 'Saving your ritual…',
    note: 'No password. We’ll email your sign-in link.',
    error: 'We couldn’t save your ritual. Please try again.',
    invalidEmail: 'That doesn’t look like a working email address.',
    errorRateLimited: 'That’s a few tries in a row — give it a minute and try again.',
    errorOffline: 'You appear to be offline. Check your connection and try again.',
    errorUnavailable: 'We can’t save it right now. Please try again in a moment.',
  },

  result: {
    eyebrow: 'Your first ritual is ready',
    reflectEyebrow: 'Built from your answers',
    reflectBody: 'Built from what you just told us.',
    sample: 'Your first question',
    mix: 'Your question mix',
    minutes: 'minutes',
    yourPace: 'Your pace',
    lighter: 'Make it lighter',
    deeper: 'Take us deeper',
    whyTitle: 'Why this works',
    whyBody: 'Questions that open gradually, answered by both of you. You set the pace; passing is always allowed.',
    transparent: 'A recommendation from your choices — not a personality, compatibility or psychological score.',
    cta: 'Save my ritual',
    paces: { gentle: 'gentle pace', bold: 'bold pace', balanced: 'balanced pace' },
    depths: { light: 'Light', playful: 'Playful', deep: 'Deep', bold: 'Bold' },
    contexts: {
      partner: 'Your partner',
      date: 'A date',
      new_person: 'Someone new',
      friend: 'A friend',
      unsure: 'Whoever’s with you',
    },
    boundaries_one: '{{count}} topic off the table',
    boundaries_other: '{{count}} topics off the table',
    barrierAnswer: {
      questions: 'You said you never know what to ask. You won’t have to.',
      distraction: 'You said phones get in the way. This one puts the question up and then gets out of the way.',
      time_energy: 'You said time and energy are short. This fits in {{minutes}} minutes.',
      repetition: 'You said the conversations repeat. This one won’t be the same twice.',
      awkward_vulnerability: 'You said vulnerability feels awkward. Nothing here goes further than you both allow.',
      none: '',
    },
  },

  // Paid mode only. Hidden entirely while FUNNEL_MODE=waitlist, but kept intact
  // so flipping the server flag at launch restores a working checkout.
  offer: {
    eyebrow: 'A founding-member offer',
    title: 'Keep every way of getting closer',
    body: 'Your ritual is saved. Add the Premium Pass now — one payment, yours for a year, and your partner always joins free.',
    save: '20% off today',
    oneTime: 'One-time payment · does not auto-renew',
    taxNote: 'Tax included. Your total is confirmed at checkout.',
    buy: 'Get Premium',
    freeBadge: 'FREE',
    freeEyebrow: 'Your card unlocked this',
    freeTitle: 'Your Premium is on us',
    freeBody: 'Your card includes 3 months of Bonded Premium — no payment needed. Activate it and it lands on the account you just created.',
    activateFree: 'Activate free Premium',
    haveCode: 'Have a code from your card?',
    codePlaceholder: 'BONDED-XXXX-XXXX-XXXX',
    redeem: 'Redeem code',
    redeemError: 'We couldn’t redeem this code. Check it and try again.',
    redeemed: 'Your free Premium is active. Opening Bonded…',
    later: 'Maybe later — just get the app',
    secure: 'Secure checkout by Paddle',
    unavailable: 'Checkout opens soon. You can still redeem a card code or get the app now.',
    benefits: {
      experiences: 'Every new Experience',
      rituals: 'Guided rituals for any mood',
      memories: 'Memory Capsules worth keeping',
      insights: 'Insights from your time together',
      hosting: 'Unlimited hosting',
      partner: 'Your partner joins free',
    },
  },

  waitlist: {
    eyebrow: 'You’re in',
    title: 'You’re on the list.',
    position: 'You’re number {{position}}',
    positionUnknown: 'We’ve saved your place.',
    body: 'We’ll email you the moment Bonded opens.',
    savedTitle: 'What we saved for you',
    deck: {
      eyebrow: 'While you wait',
      title: 'The app is coming. Your first conversation isn’t.',
      // Product facts from store.bonded.studio/l/closeness-deck — keep these
      // accurate; "for couples" is part of the product's own name.
      title2: 'The Closeness Deck',
      body: '270+ questions, dares and rituals for couples. A 60-page PDF, instant download.',
      cta: 'Get the deck',
      note: 'Opens our store in a new tab.',
    },
    skip: 'Maybe later — just notify me',
  },

  handoff: {
    title: 'Check your email',
    body: 'Your sign-in link and your place in line are on their way.',
    waitTitle: 'Your ritual is saved',
    open: 'Open in Bonded',
    store: 'Download on the App Store',
    storeAndroid: 'Get it on Google Play (Android early access)',
    email: 'A secure sign-in link is on its way.',
  },

  errors: {
    generic: 'Something went wrong. Please try again.',
    offline: 'You appear to be offline. Check your connection and try again.',
    expired: 'This session expired. Starting a fresh one…',
  },
} as const

export type Translation = typeof en
